
import {
  computed, defineComponent, PropType, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import { CountryCode } from 'libphonenumber-js';
import CountryDropdown from '@/components/fields/CountryDropdown.vue';
import { UserState } from '@/composables/User';
import {
  CountryAndStateConfig,
  CountryAndStateValues,
} from '@/types/country-state-types';
import { countriesPhonesCodes } from '@/configs/countries';
import {
  RcsAgent,
  RcsOnboardingStatus,
  RcsSelectableSender,
  normalizeRcsTestDevicePhoneNumber,
} from '@/types/rcs-onboarding';

export default defineComponent({
  name: 'RcsTestRecipientPanel',

  components: {
    Dropdown,
    Button,
    InputText,
    CountryDropdown,
  },

  props: {
    senderOptions: {
      type: Array as PropType<RcsSelectableSender[]>,
      default: () => [],
    },
    selectedSenderId: {
      type: String,
      default: '',
    },
    selectedAgent: {
      type: Object as PropType<RcsAgent | null>,
      default: null,
    },
    availableTestNumbers: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    selectedTestNumbers: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    sessionExtraNumbers: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    isSharedSender: {
      type: Boolean,
      default: true,
    },
    canPersist: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'update:selectedSenderId',
    'update:selectedTestNumbers',
    'add-test-number',
    'persist-test-numbers',
  ],

  setup(props, { emit }) {
    const { t } = useI18n();
    const newNumberLocal = ref('');
    const isSaving = ref(false);
    const countryAndState = ref<CountryAndStateValues>({
      country: (UserState.user.country ?? 'FR').toUpperCase() as CountryCode,
      state: '',
    });
    const countryDropdownConfig: CountryAndStateConfig = {
      showStates: false,
      showLabels: false,
      showFlags: true,
      showPhonesCodes: true,
      showOnlyPhoneCodeForSelection: true,
    };

    const selectedAgentDraft = computed(
      () => props.selectedAgent
        && props.selectedAgent.onboarding_status !== RcsOnboardingStatus.LAUNCHED,
    );

    const resolveSenderLabel = (senderId: string) => {
      const match = props.senderOptions.find((sender) => sender.sender_id === senderId);
      return match?.display_name || senderId;
    };

    const onSenderChange = (senderId: string) => {
      emit('update:selectedSenderId', senderId);
      emit('update:selectedTestNumbers', []);
    };

    const toggleNumber = (number: string) => {
      const next = props.selectedTestNumbers.includes(number)
        ? props.selectedTestNumbers.filter((n) => n !== number)
        : [...props.selectedTestNumbers, number];
      emit('update:selectedTestNumbers', next);
    };

    const buildE164FromLocal = (local: string): string => {
      const digits = local.replace(/\D/g, '');
      if (!digits) return '';
      const country = countryAndState.value.country?.toUpperCase() || '';
      const dialCode = countriesPhonesCodes[country] || '';
      const dialDigits = dialCode.replace(/\D/g, '');
      if (digits.startsWith(dialDigits)) {
        return normalizeRcsTestDevicePhoneNumber(`+${digits}`);
      }
      return normalizeRcsTestDevicePhoneNumber(`+${dialDigits}${digits}`);
    };

    const addNumber = () => {
      const normalized = buildE164FromLocal(newNumberLocal.value);
      if (!normalized) return;
      emit('add-test-number', normalized);
      newNumberLocal.value = '';
    };

    const saveForAgent = async () => {
      isSaving.value = true;
      try {
        emit('persist-test-numbers');
      } finally {
        isSaving.value = false;
      }
    };

    return {
      t,
      newNumberLocal,
      isSaving,
      countryAndState,
      countryDropdownConfig,
      selectedAgentDraft,
      resolveSenderLabel,
      onSenderChange,
      toggleNumber,
      addNumber,
      saveForAgent,
    };
  },
});
